import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Button, Card, CardHeader, CardBody } from '@unwritten/ui-primitives';
import { z } from 'zod';
export const BuilderWizard = ({ steps, initialData = {}, onComplete, onCancel, persistenceKey, showProgress = true, allowStepNavigation = true, className = '', }) => {
    // Current step index
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    // Wizard data
    const [data, setData] = useState(() => {
        // Try to load from localStorage if persistence key is provided
        if (persistenceKey && typeof window !== 'undefined') {
            const saved = localStorage.getItem(persistenceKey);
            if (saved) {
                try {
                    return { ...initialData, ...JSON.parse(saved) };
                }
                catch {
                    return initialData;
                }
            }
        }
        return initialData;
    });
    // Validation errors for current step
    const [errors, setErrors] = useState({});
    // Submission state
    const [submitting, setSubmitting] = useState(false);
    // Step completion status
    const [completedSteps, setCompletedSteps] = useState(new Set());
    // Current step (with safety check)
    const currentStep = steps[currentStepIndex];
    if (!currentStep) {
        throw new Error(`Invalid step index: ${currentStepIndex}`);
    }
    // Persist data to localStorage
    useEffect(() => {
        if (persistenceKey && typeof window !== 'undefined') {
            localStorage.setItem(persistenceKey, JSON.stringify(data));
        }
    }, [data, persistenceKey]);
    // Call onEnter when step changes
    useEffect(() => {
        if (currentStep.onEnter) {
            currentStep.onEnter(data);
        }
    }, [currentStepIndex, currentStep, data]);
    // Update step data
    const updateStepData = useCallback((stepId, stepData) => {
        setData(prev => ({ ...prev, [stepId]: stepData }));
        setErrors({});
    }, []);
    // Validate current step
    const validateCurrentStep = useCallback(async () => {
        const stepData = data[currentStep.id];
        // Schema validation
        if (currentStep.schema) {
            try {
                currentStep.schema.parse(stepData);
            }
            catch (error) {
                if (error instanceof z.ZodError) {
                    const fieldErrors = {};
                    error.errors.forEach(err => {
                        const path = err.path.join('.');
                        fieldErrors[path] = err.message;
                    });
                    setErrors(fieldErrors);
                    return false;
                }
            }
        }
        // Custom validation
        if (currentStep.validate) {
            const result = await currentStep.validate(stepData);
            if (!result.valid) {
                setErrors(result.errors || {});
                return false;
            }
        }
        setErrors({});
        return true;
    }, [currentStep, data]);
    // Navigate to step
    const goToStep = useCallback(async (index) => {
        if (index < 0 || index >= steps.length)
            return;
        // Call onLeave hook
        if (currentStep.onLeave) {
            await currentStep.onLeave(data);
        }
        setCurrentStepIndex(index);
        setErrors({});
    }, [currentStep, data, steps.length]);
    // Go to next step
    const handleNext = useCallback(async () => {
        const valid = await validateCurrentStep();
        if (!valid)
            return;
        // Mark current step as completed
        setCompletedSteps(prev => new Set(prev).add(currentStepIndex));
        if (currentStepIndex < steps.length - 1) {
            await goToStep(currentStepIndex + 1);
        }
        else {
            // Last step - submit
            await handleSubmit();
        }
    }, [currentStepIndex, steps.length, validateCurrentStep, goToStep]);
    // Go to previous step
    const handleBack = useCallback(async () => {
        if (currentStepIndex > 0) {
            await goToStep(currentStepIndex - 1);
        }
    }, [currentStepIndex, goToStep]);
    // Submit wizard
    const handleSubmit = useCallback(async () => {
        setSubmitting(true);
        try {
            await onComplete(data);
            // Clear persistence
            if (persistenceKey && typeof window !== 'undefined') {
                localStorage.removeItem(persistenceKey);
            }
        }
        finally {
            setSubmitting(false);
        }
    }, [data, onComplete, persistenceKey]);
    // Cancel wizard
    const handleCancel = useCallback(() => {
        if (onCancel) {
            onCancel();
        }
        // Clear persistence
        if (persistenceKey && typeof window !== 'undefined') {
            localStorage.removeItem(persistenceKey);
        }
    }, [onCancel, persistenceKey]);
    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (event) => {
            // Ctrl/Cmd + Enter to continue
            if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
                event.preventDefault();
                handleNext();
            }
            // Escape to cancel
            if (event.key === 'Escape') {
                event.preventDefault();
                handleCancel();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleNext, handleCancel]);
    // Convert steps to stepper panel format
    const stepperSteps = useMemo(() => {
        return steps.map((step, index) => {
            let status;
            if (index < currentStepIndex) {
                status = 'completed';
            }
            else if (index === currentStepIndex) {
                status = 'active';
            }
            else {
                status = 'pending';
            }
            return {
                id: step.id,
                label: step.title,
                description: step.description,
                status,
                optional: step.optional,
            };
        });
    }, [steps, currentStepIndex]);
    // Progress percentage
    const progressPercent = useMemo(() => {
        return Math.round(((currentStepIndex + 1) / steps.length) * 100);
    }, [currentStepIndex, steps.length]);
    // Can navigate to step
    const canNavigateToStep = useCallback((index) => {
        if (!allowStepNavigation)
            return false;
        // Can only navigate to completed steps or current step
        return index <= currentStepIndex || completedSteps.has(index);
    }, [allowStepNavigation, currentStepIndex, completedSteps]);
    return (_jsxs("div", { className: `builder-wizard ${className}`, children: [_jsxs("div", { className: "mb-6", children: [_jsxs("div", { className: "flex items-center justify-between mb-4", children: [_jsxs("div", { children: [_jsx("h1", { className: "text-3xl font-bold text-uw-text-primary", children: currentStep.title }), currentStep.description && (_jsx("p", { className: "text-uw-text-secondary mt-1", children: currentStep.description }))] }), showProgress && (_jsxs("div", { className: "text-right", children: [_jsxs("div", { className: "text-sm text-uw-text-tertiary mb-1", children: ["Step ", currentStepIndex + 1, " of ", steps.length] }), _jsxs("div", { className: "text-2xl font-bold text-uw-text-primary", children: [progressPercent, "%"] })] }))] }), showProgress && (_jsx("div", { className: "w-full bg-uw-surface-elevated rounded-full h-2 overflow-hidden", children: _jsx("div", { className: "bg-uw-interactive-primary h-full transition-all duration-300", style: { width: `${progressPercent}%` } }) }))] }), _jsxs("div", { className: "grid grid-cols-1 lg:grid-cols-4 gap-6", children: [_jsxs("div", { className: "lg:col-span-1", children: [_jsx(Card, { children: _jsx(CardBody, { className: "p-4", children: _jsx("div", { className: "space-y-1", children: stepperSteps.map((step, index) => {
                                            const isActive = index === currentStepIndex;
                                            const isCompleted = index < currentStepIndex;
                                            const isClickable = canNavigateToStep(index);
                                            return (_jsxs("button", { type: "button", onClick: () => {
                                                    if (isClickable) {
                                                        goToStep(index);
                                                    }
                                                }, disabled: !isClickable && !isActive, className: `w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${isActive
                                                    ? 'bg-uw-interactive-primary text-uw-text-inverse'
                                                    : isClickable
                                                        ? 'hover:bg-uw-surface-elevated cursor-pointer'
                                                        : 'opacity-50 cursor-not-allowed'}`, children: [_jsx("div", { className: `flex items-center justify-center w-8 h-8 rounded-full flex-shrink-0 ${isActive
                                                            ? 'bg-white/20 text-uw-text-inverse font-semibold'
                                                            : isCompleted
                                                                ? 'bg-uw-success text-uw-text-inverse'
                                                                : 'bg-uw-surface-elevated text-uw-text-tertiary border border-uw-border-default'}`, children: isCompleted ? (_jsx("svg", { className: "w-4 h-4", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M5 13l4 4L19 7" }) })) : (_jsx("span", { className: "text-sm font-medium", children: index + 1 })) }), _jsxs("div", { className: "flex-1 text-left", children: [_jsx("div", { className: `text-sm font-medium ${isActive ? 'text-uw-text-inverse' : 'text-uw-text-primary'}`, children: step.label }), step.description && (_jsx("div", { className: `text-xs mt-0.5 ${isActive ? 'text-white/70' : 'text-uw-text-tertiary'}`, children: step.description })), step.optional && (_jsx("span", { className: `text-xs ${isActive ? 'text-white/70' : 'text-uw-text-tertiary'}`, children: "(Optional)" }))] })] }, step.id));
                                        }) }) }) }), _jsx(Card, { className: "mt-4", children: _jsxs(CardBody, { children: [_jsx("h3", { className: "text-sm font-semibold text-uw-text-primary mb-2", children: "Keyboard Shortcuts" }), _jsxs("dl", { className: "text-xs text-uw-text-tertiary space-y-1", children: [_jsxs("div", { className: "flex justify-between", children: [_jsx("dt", { children: "Continue" }), _jsx("dd", { className: "font-mono", children: "Ctrl+Enter" })] }), _jsxs("div", { className: "flex justify-between", children: [_jsx("dt", { children: "Cancel" }), _jsx("dd", { className: "font-mono", children: "Esc" })] })] })] }) })] }), _jsx("div", { className: "lg:col-span-3", children: _jsxs(Card, { children: [_jsxs(CardHeader, { children: [_jsx("h2", { className: "text-xl font-semibold text-uw-text-primary", children: currentStep.title }), currentStep.description && (_jsx("p", { className: "text-sm text-uw-text-secondary mt-1", children: currentStep.description }))] }), _jsxs(CardBody, { children: [Object.keys(errors).length > 0 && (_jsxs("div", { className: "mb-4 p-4 bg-uw-error-surface border border-uw-error rounded-lg", children: [_jsx("h3", { className: "font-medium text-uw-error mb-2", children: "Please fix the following errors:" }), _jsx("ul", { className: "list-disc list-inside text-sm text-uw-text-secondary space-y-1", children: Object.entries(errors).map(([field, message]) => (_jsx("li", { children: message }, field))) })] })), _jsx("div", { className: "mb-6", children: currentStep.render(data[currentStep.id], (stepData) => updateStepData(currentStep.id, stepData), errors) }), _jsxs("div", { className: "flex items-center justify-between pt-4 border-t border-uw-border", children: [_jsx("div", { children: currentStepIndex > 0 && (_jsx(Button, { onClick: handleBack, variant: "secondary", disabled: submitting, children: "\u2190 Back" })) }), _jsxs("div", { className: "flex gap-3", children: [_jsx(Button, { onClick: handleCancel, variant: "ghost", disabled: submitting, children: "Cancel" }), currentStep.optional && currentStepIndex < steps.length - 1 && (_jsx(Button, { onClick: () => goToStep(currentStepIndex + 1), variant: "secondary", disabled: submitting, children: "Skip" })), _jsx(Button, { onClick: handleNext, variant: "primary", disabled: submitting, children: submitting ? 'Processing...' : (currentStepIndex < steps.length - 1
                                                                ? (currentStep.nextLabel || 'Next →')
                                                                : 'Complete') })] })] })] })] }) })] })] }));
};
BuilderWizard.displayName = 'BuilderWizard';
//# sourceMappingURL=BuilderWizard.js.map